import { InputState, Rectangle } from '../../types/game.types';
import { BaseGame } from '../BaseGame';
import { RenderManager } from '../../core/RenderManager';
import { AudioManager } from '../../core/AudioManager';
import { Player } from './entities/Player';
import { Bullet } from './entities/Bullet';
import { Mushroom } from './entities/Mushroom';
import { Centipede } from './entities/Centipede';
import { Spider } from './entities/Spider';
import { Flea } from './entities/Flea';
import { MushroomField } from './systems/MushroomField';
import { EnemyManager } from './systems/EnemyManager';

export class CentipedeGame extends BaseGame {
  private player: Player | null = null;
  private bullets: Bullet[] = [];
  private mushrooms: Mushroom[] = [];
  private centipedes: Centipede[] = [];
  private spiders: Spider[] = [];
  private fleas: Flea[] = [];
  
  private mushroomField: MushroomField;
  private enemyManager: EnemyManager;
  private nextBulletId: number = 1;
  
  // Game constants
  private readonly GRID_SIZE: number = 16;
  private readonly PLAYER_ZONE_HEIGHT: number = 80; // Bottom area for player movement
  
  // Game state
  private gameStarted: boolean = false;
  private waveComplete: boolean = false;
  private centipedeSpeed: number = 60; // Initial speed

  constructor(renderManager: RenderManager, audioManager: AudioManager) {
    const gameConfig = { width: 600, height: 900, fps: 60, scale: 1 }; // Portrait for mobile
    super(renderManager, audioManager, gameConfig, 'centipede');
    
    this.mushroomField = new MushroomField(
      this.config.width, 
      this.config.height, 
      this.GRID_SIZE,
      renderManager
    );
    this.enemyManager = new EnemyManager(
      this.config.width,
      this.config.height,
      this.GRID_SIZE,
      renderManager,
      audioManager
    );
  }

  public init(): void {
    console.log('Centipede Game initialized');
    
    // Reset game state
    this.score = 0;
    this.lives = 3;
    this.level = 1;
    this.isGameOver = false;
    this.gameStarted = true;
    this.waveComplete = false;
    this.centipedeSpeed = 60;
    
    // Clear arrays
    this.bullets = [];
    this.centipedes = [];
    this.spiders = [];
    this.fleas = [];
    this.nextBulletId = 1;
    
    // Create player
    this.createPlayer();
    
    // Initialize mushroom field
    this.mushroomField.generateField();
    this.mushrooms = this.mushroomField.getMushrooms();
    
    // Initialize enemies for this wave
    this.enemyManager.initializeWave(this.level, this.centipedeSpeed);
    this.centipedes = this.enemyManager.getCentipedes();
  }

  private createPlayer(): void {
    const startX = this.config.width / 2;
    const startY = this.config.height - this.PLAYER_ZONE_HEIGHT + 20; // Position in player zone at bottom
    
    
    this.player = new Player(
      'player',
      startX,
      startY,
      this.renderManager,
      this.audioManager,
      this.config.width,
      this.config.height,
      this.PLAYER_ZONE_HEIGHT
    );
  }

  public update(deltaTime: number): void {
    if (this.isGameOver) return;
    
    if (!this.gameStarted || !this.player) return;
    
    // Update player
    if (this.player.active) {
      this.player.update(deltaTime);
    }
    
    // Update bullets
    this.updateBullets(deltaTime);
    
    // Update enemies
    this.updateEnemies(deltaTime);
    
    // Check collisions
    this.checkCollisions();
    
    // Check win/lose conditions
    this.checkWaveConditions();
  }

  private updateBullets(deltaTime: number): void {
    this.bullets.forEach(bullet => bullet.update(deltaTime));
    this.bullets = this.bullets.filter(bullet => bullet.active);
  }

  private updateEnemies(deltaTime: number): void {
    // Update centipedes
    this.centipedes.forEach(centipede => {
      centipede.setMushrooms(this.mushrooms);
      centipede.update(deltaTime);
    });
    this.centipedes = this.centipedes.filter(centipede => centipede.active);
    
    // Update spiders
    this.spiders.forEach(spider => spider.update(deltaTime));
    this.spiders = this.spiders.filter(spider => spider.active);
    
    // Update fleas
    this.fleas.forEach(flea => {
      flea.setMushroomField(this.mushroomField);
      flea.update(deltaTime);
    });
    this.fleas = this.fleas.filter(flea => flea.active);
    
    // Update enemy manager for spawning
    this.enemyManager.update(deltaTime, this.centipedes, this.spiders, this.fleas);
    
    // Get updated enemy arrays
    const newCentipedes = this.enemyManager.getCentipedes();
    const newSpiders = this.enemyManager.getSpiders();
    const newFleas = this.enemyManager.getFleas();
    
    // Add new enemies to our arrays
    newCentipedes.forEach(centipede => {
      if (!this.centipedes.find(c => c.id === centipede.id)) {
        this.centipedes.push(centipede);
      }
    });
    
    newSpiders.forEach(spider => {
      if (!this.spiders.find(s => s.id === spider.id)) {
        this.spiders.push(spider);
      }
    });
    
    newFleas.forEach(flea => {
      if (!this.fleas.find(f => f.id === flea.id)) {
        this.fleas.push(flea);
      }
    });
  }

  private checkCollisions(): void {
    if (!this.player || !this.player.active) return;

    // Check bullet vs enemy collisions
    this.bullets.forEach(bullet => {
      if (!bullet.active) return;

      // Check vs centipedes
      this.centipedes.forEach(centipede => {
        if (!centipede.active) return;
        
        const hitSegment = centipede.checkBulletCollision(bullet.getBounds());
        if (hitSegment !== -1) {
          bullet.destroy();
          const points = centipede.hitSegment(hitSegment, this.mushroomField);
          this.addScore(points);
          this.audioManager.playExplosion();
        }
      });

      // Check vs spiders
      this.spiders.forEach(spider => {
        if (!spider.active) return;
        
        if (this.checkRectCollision(bullet.getBounds(), spider.getBounds())) {
          bullet.destroy();
          spider.destroy();
          this.addScore(spider.getPointValue());
          this.audioManager.playExplosion();
        }
      });

      // Check vs fleas
      this.fleas.forEach(flea => {
        if (!flea.active) return;
        
        if (this.checkRectCollision(bullet.getBounds(), flea.getBounds())) {
          bullet.destroy();
          flea.destroy();
          this.addScore(flea.getPointValue());
          this.audioManager.playExplosion();
        }
      });

      // Check vs mushrooms
      this.mushrooms.forEach(mushroom => {
        if (!mushroom.active) return;
        
        if (this.checkRectCollision(bullet.getBounds(), mushroom.getBounds())) {
          bullet.destroy();
          const points = mushroom.hit();
          this.addScore(points);
          
          if (!mushroom.active) {
            this.audioManager.playExplosion();
          }
        }
      });
    });

    // Check player vs enemy collisions
    const playerBounds = this.player.getBounds();
    
    // Check vs centipedes
    this.centipedes.forEach(centipede => {
      if (!centipede.active) return;
      
      if (centipede.checkPlayerCollision(playerBounds)) {
        this.loseLife();
      }
    });

    // Check vs spiders
    this.spiders.forEach(spider => {
      if (!spider.active) return;
      
      if (this.checkRectCollision(playerBounds, spider.getBounds())) {
        this.loseLife();
        spider.destroy();
      }
    });

    // Check vs fleas
    this.fleas.forEach(flea => {
      if (!flea.active) return;
      
      if (this.checkRectCollision(playerBounds, flea.getBounds())) {
        this.loseLife();
        flea.destroy();
      }
    });
  }

  private checkRectCollision(rect1: Rectangle, rect2: Rectangle): boolean {
    return rect1.x < rect2.x + rect2.width &&
           rect1.x + rect1.width > rect2.x &&
           rect1.y < rect2.y + rect2.height &&
           rect1.y + rect1.height > rect2.y;
  }

  private checkWaveConditions(): void {
    // Wave complete when all centipedes are destroyed
    const activeCentipedes = this.centipedes.filter(c => c.active);
    
    if (activeCentipedes.length === 0 && !this.waveComplete) {
      this.waveComplete = true;
      this.completeWave();
    }
  }

  private completeWave(): void {
    console.log(`Wave ${this.level} complete!`);
    
    // Bonus points for remaining mushrooms
    const mushroomBonus = this.mushrooms.filter(m => m.active).length * 5;
    this.addScore(mushroomBonus);
    
    // Start next wave after delay
    setTimeout(() => {
      this.nextLevel();
      this.startNextWave();
    }, 2000);
  }

  private startNextWave(): void {
    this.waveComplete = false;
    this.centipedeSpeed += 10; // Increase speed each wave
    
    // Clear enemies
    this.centipedes = [];
    this.spiders = [];
    this.fleas = [];
    
    // Regenerate some mushrooms
    this.mushroomField.regenerateField();
    this.mushrooms = this.mushroomField.getMushrooms();
    
    // Initialize enemies for new wave
    this.enemyManager.initializeWave(this.level, this.centipedeSpeed);
    this.centipedes = this.enemyManager.getCentipedes();
  }

  public render(ctx: CanvasRenderingContext2D): void {
    // Clear screen with dark background
    this.renderManager.clear('#000000');
    
    // Draw game area border (commented out for cleaner look)
    // this.renderManager.strokeRect(0, 0, this.config.width, this.config.height, '#00FF00', 2);
    
    // Draw player zone separator
    const playerZoneY = this.config.height - this.PLAYER_ZONE_HEIGHT;
    this.renderManager.drawLine(0, playerZoneY, this.config.width, playerZoneY, '#00FF00', 1);
    
    // Render mushrooms
    this.mushrooms.forEach(mushroom => {
      if (mushroom.active) {
        mushroom.render(ctx);
      }
    });
    
    // Render enemies
    this.centipedes.forEach(centipede => {
      if (centipede.active) {
        centipede.render(ctx);
      }
    });
    
    this.spiders.forEach(spider => {
      if (spider.active) {
        spider.render(ctx);
      }
    });
    
    this.fleas.forEach(flea => {
      if (flea.active) {
        flea.render(ctx);
      }
    });
    
    // Render bullets
    this.bullets.forEach(bullet => {
      if (bullet.active) {
        bullet.render(ctx);
      }
    });
    
    // Render player
    if (this.player && this.player.active) {
      this.player.render(ctx);
    }
    
    // Render HUD
    this.renderHUD();
    
    // Render game state messages
    if (this.waveComplete) {
      this.renderWaveComplete();
    }
    
    if (this.isGameOver) {
      this.renderGameOver();
    }
    
    // Render initial entry if showing
    if (this.showingInitialEntry && this.initialEntry) {
      this.initialEntry.render(ctx, this.config.width / 2, this.config.height / 2);
    }
  }

  protected renderHUD(): void {
    // Score
    this.renderManager.drawText(
      `SCORE: ${this.score.toString().padStart(6, '0')}`,
      20,
      20,
      '#FFFFFF',
      '16px "Courier New", monospace'
    );
    
    // Lives
    this.renderManager.drawText('LIVES: ', this.config.width - 200, 20, '#FFFFFF', '16px "Courier New", monospace');
    for (let i = 0; i < this.lives; i++) {
      this.renderManager.drawC64Char('▲', this.config.width - 120 + i * 20, 18, '#00FF00', 1);
    }
    
    // Wave
    this.renderManager.drawTextCentered(
      `WAVE ${this.level}`,
      this.config.width / 2,
      20,
      '#FFFF00',
      '16px "Courier New", monospace'
    );
  }

  private renderWaveComplete(): void {
    const centerX = this.config.width / 2;
    const centerY = this.config.height / 2;
    
    this.renderManager.fillRect(0, 0, this.config.width, this.config.height, 'rgba(0, 0, 0, 0.7)');
    
    this.renderManager.drawTextCentered(
      `WAVE ${this.level} COMPLETE!`,
      centerX,
      centerY - 40,
      '#00FF00',
      '32px "Courier New", monospace'
    );
    
    const mushroomBonus = this.mushrooms.filter(m => m.active).length * 5;
    
    this.renderManager.drawTextCentered(
      `MUSHROOM BONUS: ${mushroomBonus}`,
      centerX,
      centerY + 20,
      '#FFFF00',
      '18px "Courier New", monospace'
    );
  }

  public handleInput(input: InputState): void {
    if (this.isGameOver) {
      if (this.showingInitialEntry) {
        return;
      }
      
      if (input.keys['Space'] || input.touch.gesture === 'tap') {
        this.restart();
      }
      return;
    }
    
    if (!this.player || !this.player.active) return;
    
    // Player movement
    if (input.keys['ArrowLeft'] || input.keys['KeyA']) {
      this.player.moveLeft();
    }
    if (input.keys['ArrowRight'] || input.keys['KeyD']) {
      this.player.moveRight();
    }
    if (input.keys['ArrowUp'] || input.keys['KeyW']) {
      this.player.moveUp();
    }
    if (input.keys['ArrowDown'] || input.keys['KeyS']) {
      this.player.moveDown();
    }
    
    // Shooting
    if (input.keys['Space'] || input.touch.gesture === 'tap') {
      const bulletData = this.player.shoot();
      if (bulletData) {
        this.createBullet(bulletData.x, bulletData.y);
      }
    }
  }

  private createBullet(x: number, y: number): void {
    const bullet = new Bullet(
      `bullet_${this.nextBulletId++}`,
      x,
      y,
      this.renderManager,
      this.config.height
    );
    this.bullets.push(bullet);
  }

  public cleanup(): void {
    super.cleanup();
    this.bullets = [];
    this.centipedes = [];
    this.spiders = [];
    this.fleas = [];
    this.mushrooms = [];
    this.player = null;
  }
}